import { memo, useState, useCallback } from 'react';

import ReactPlayer from 'react-player';
import { FiPlay } from 'react-icons/fi';

import Controls from './Controls';
import { useVideoPlayer } from './hooks/useVideoPlayer';
import * as S from './styles';

type VideoPlayerProps = {
  url: string;
  thumbnail?: string | null;
  autoPlay?: boolean;
  isLive?: boolean;
  controlsLocked?: boolean;
  extraControls?: React.ReactNode;
  onReady?: () => void;
  onError?: (error: unknown) => void;
};

const VideoPlayer = ({
  url,
  thumbnail,
  autoPlay = true,
  isLive = false,
  controlsLocked = false,
  extraControls,
  onReady,
  onError,
}: VideoPlayerProps) => {
  const [hasStarted, setHasStarted] = useState(autoPlay);

  const {
    playerRef,
    containerRef,
    isPlaying,
    isMuted,
    volume,
    played,
    loaded,
    duration,
    isBuffering,
    isFullscreen,
    showControls,
    isReady,
    isAtLiveEdge,
    togglePlay,
    play,
    seekToLive,
    toggleMute,
    handleVolumeChange,
    handleProgress,
    handleSeekStart,
    handleSeek,
    handleSeekEnd,
    handleDuration,
    handleBuffer,
    handleBufferEnd,
    handleReady,
    handleStart: handlePlayerStart,
    handleError,
    toggleFullscreen,
    handleMouseMove,
    handleMouseLeave,
    formatTime,
    currentTime,
  } = useVideoPlayer({
    autoPlay: autoPlay && hasStarted,
    controlsLocked,
    onReady,
    onError,
  });

  const handleClickOverlay = useCallback(() => {
    if (!hasStarted) {
      setHasStarted(true);
      play();
    } else {
      togglePlay();
    }
  }, [hasStarted, play, togglePlay]);

  const handleStart = useCallback(() => {
    setHasStarted(true);
  }, []);

  // Show thumbnail until video starts
  if (!hasStarted && thumbnail) {
    return (
      <S.Container ref={containerRef} $showControls>
        <S.Thumbnail $src={thumbnail}>
          <S.PlayOverlay onClick={handleStart} type="button">
            <FiPlay />
          </S.PlayOverlay>
        </S.Thumbnail>
      </S.Container>
    );
  }

  return (
    <S.Container
      ref={containerRef}
      $showControls={showControls}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
    >
      {/* React Player */}
      <ReactPlayer
        ref={playerRef}
        url={url}
        playing={isPlaying}
        muted={isMuted}
        volume={volume}
        width="100%"
        height="100%"
        controls={false}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onBuffer={handleBuffer}
        onBufferEnd={handleBufferEnd}
        onReady={handleReady}
        onStart={handlePlayerStart}
        onError={handleError}
        progressInterval={100}
        config={{
          youtube: {
            playerVars: {
              autoplay: 1,
              mute: 1,
              modestbranding: 1,
              rel: 0,
              showinfo: 0,
              iv_load_policy: 3,
              disablekb: 1,
              controls: 0,
              playsinline: 1,
              fs: 0,
              cc_load_policy: 0,
              origin: window.location.origin,
            },
          },
          vimeo: {
            playerOptions: {
              autoplay: true,
              muted: true,
              title: false,
              byline: false,
              portrait: false,
              controls: false,
              dnt: true,
              transparent: false,
            },
          },
          file: {
            attributes: {
              autoPlay: true,
              controlsList: 'nodownload',
              disablePictureInPicture: true,
            },
          },
        }}
      />

      {/* Click to play/pause overlay */}
      <S.ClickOverlay onClick={handleClickOverlay} />

      {/* Loading spinner */}
      {isBuffering && (
        <S.LoadingOverlay>
          <S.Spinner />
        </S.LoadingOverlay>
      )}

      {/* Big play button when paused */}
      {!isPlaying && isReady && (
        <S.BigPlayButton onClick={togglePlay} type="button">
          <FiPlay />
        </S.BigPlayButton>
      )}

      {/* Custom controls */}
      <Controls
        isPlaying={isPlaying}
        isMuted={isMuted}
        volume={volume}
        played={played}
        loaded={loaded}
        duration={duration}
        currentTime={currentTime}
        isFullscreen={isFullscreen}
        isVisible={showControls}
        isLive={isLive}
        isAtLiveEdge={isAtLiveEdge}
        onSeekToLive={seekToLive}
        onTogglePlay={togglePlay}
        onToggleMute={toggleMute}
        onVolumeChange={handleVolumeChange}
        onSeekStart={handleSeekStart}
        onSeek={handleSeek}
        onSeekEnd={handleSeekEnd}
        onToggleFullscreen={toggleFullscreen}
        formatTime={formatTime}
        extraControls={extraControls}
      />
    </S.Container>
  );
};

export default memo(VideoPlayer);
